/**
 * Address Manager Component
 * Manage saved shipping/billing addresses
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/styled/Card';
import { PrimaryButton, SecondaryButton, DangerButton } from '../../../components/styled/Button';
import { Flex, Grid } from '../../../components/styled/Layout';
import { Input, Select, FormLabel } from '../../../components/styled/Input';
import apiService from '../../../services/apiService';

const AddressManager = ({ onSelectAddress, showSelection = false }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    streetAddress: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    phone: '',
    isDefault: false
  });

  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = async () => {
    try {
      setLoading(true);
      const data = await apiService.get('/shop/addresses');
      setAddresses(data || []);
    } catch (error) {
      console.error('Error loading addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await apiService.put(`/shop/addresses/${editingId}`, formData);
        alert('Address updated successfully!');
      } else {
        await apiService.post('/shop/addresses', formData);
        alert('Address saved successfully!');
      }
      
      resetForm();
      await loadAddresses();
    } catch (error) {
      console.error('Error saving address:', error);
      alert('Failed to save address. Please try again.');
    }
  };

  const handleEdit = (address) => {
    setFormData({
      fullName: address.fullName,
      streetAddress: address.streetAddress,
      apartment: address.apartment || '',
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country,
      phone: address.phone || '',
      isDefault: address.isDefault || false
    });
    setEditingId(address.id);
    setShowForm(true);
  };

  const handleDelete = async (addressId) => {
    if (!confirm('Are you sure you want to delete this address?')) return;
    
    try {
      await apiService.delete(`/shop/addresses/${addressId}`);
      alert('Address deleted successfully!');
      await loadAddresses();
    } catch (error) {
      console.error('Error deleting address:', error);
      alert('Failed to delete address. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      streetAddress: '',
      apartment: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States',
      phone: '',
      isDefault: false
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return <p>Loading addresses...</p>;
  }

  return (
    <div>
      {!showForm && (
        <Flex justify="space-between" align="center" style={{ marginBottom: '24px' }}>
          <h2 style={{ margin: 0 }}>Saved Addresses</h2>
          <PrimaryButton onClick={() => setShowForm(true)}>
            ➕ Add New Address
          </PrimaryButton>
        </Flex>
      )}

      {/* Address Form */}
      {showForm && (
        <Card style={{ marginBottom: '24px' }}>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Address' : 'Add New Address'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Grid columns={2} gap="var(--space-4)" style={{ marginBottom: '16px' }}>
                <div>
                  <FormLabel htmlFor="fullName">Full Name *</FormLabel>
                  <Input
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <FormLabel htmlFor="phone">Phone Number</FormLabel>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </Grid>

              <div style={{ marginBottom: '16px' }}>
                <FormLabel htmlFor="streetAddress">Street Address *</FormLabel>
                <Input
                  id="streetAddress"
                  value={formData.streetAddress}
                  onChange={(e) => setFormData({ ...formData, streetAddress: e.target.value })}
                  required
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <FormLabel htmlFor="apartment">Apartment, Suite, etc.</FormLabel>
                <Input
                  id="apartment"
                  value={formData.apartment}
                  onChange={(e) => setFormData({ ...formData, apartment: e.target.value })}
                />
              </div>

              <Grid columns={3} gap="var(--space-4)" style={{ marginBottom: '16px' }}>
                <div>
                  <FormLabel htmlFor="city">City *</FormLabel>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <FormLabel htmlFor="state">State *</FormLabel>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <FormLabel htmlFor="zipCode">ZIP Code *</FormLabel>
                  <Input
                    id="zipCode"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    required
                  />
                </div>
              </Grid>

              <div style={{ marginBottom: '16px' }}>
                <FormLabel htmlFor="country">Country *</FormLabel>
                <Select
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Australia">Australia</option>
                  <option value="Other">Other</option>
                </Select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                  <span>Set as default address</span>
                </label>
              </div>

              <Flex gap="var(--space-3)">
                <PrimaryButton type="submit">
                  {editingId ? 'Update Address' : 'Save Address'}
                </PrimaryButton>
                <SecondaryButton type="button" onClick={resetForm}>
                  Cancel
                </SecondaryButton>
              </Flex>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Address List */}
      {addresses.length === 0 && !showForm ? (
        <Card>
          <CardContent>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <p style={{ fontSize: '18px', color: '#666', marginBottom: '16px' }}>
                No saved addresses yet
              </p>
              <PrimaryButton onClick={() => setShowForm(true)}>
                Add Your First Address
              </PrimaryButton>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Grid columns={showSelection ? 1 : 2} gap="var(--space-4)">
          {addresses.map(address => (
            <Card 
              key={address.id} 
              style={{ 
                position: 'relative',
                border: address.isDefault ? '2px solid var(--color-primary)' : '1px solid #e0e0e0'
              }}
            >
              <CardContent>
                {address.isDefault && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    backgroundColor: 'var(--color-primary)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    DEFAULT
                  </div>
                )}
                
                <div style={{ marginBottom: '12px' }}>
                  <p style={{ fontWeight: '600', marginBottom: '4px' }}>{address.fullName}</p>
                  <p style={{ margin: '2px 0', color: '#666' }}>{address.streetAddress}</p>
                  {address.apartment && (
                    <p style={{ margin: '2px 0', color: '#666' }}>{address.apartment}</p>
                  )}
                  <p style={{ margin: '2px 0', color: '#666' }}>
                    {address.city}, {address.state} {address.zipCode}
                  </p>
                  <p style={{ margin: '2px 0', color: '#666' }}>{address.country}</p>
                  {address.phone && (
                    <p style={{ margin: '2px 0', color: '#666' }}>Phone: {address.phone}</p>
                  )}
                </div>

                <Flex gap="var(--space-2)" wrap="wrap">
                  {showSelection && onSelectAddress && (
                    <PrimaryButton 
                      onClick={() => onSelectAddress(address)}
                      size="small"
                    >
                      Select This Address
                    </PrimaryButton>
                  )}
                  {!showSelection && (
                    <>
                      <SecondaryButton 
                        onClick={() => handleEdit(address)}
                        size="small"
                      >
                        Edit
                      </SecondaryButton>
                      <DangerButton 
                        onClick={() => handleDelete(address.id)}
                        size="small"
                      >
                        Delete
                      </DangerButton>
                    </>
                  )}
                </Flex>
              </CardContent>
            </Card>
          ))}
        </Grid>
      )}
    </div>
  );
};

export default AddressManager;
